import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '@verantos-dev/core-annotation-tool';

import { useRenderAnnotationAgentPromptQuery } from '../../api/createProjectApiSlice';
import { useIsAgentStepEnabled } from '../../hooks/useCreateProjectWizardSteps';
import {
  selectAddedConcepts,
  selectAnnotationAgentConcepts,
  selectAnnotationAgentSections,
  selectConditionArea,
} from '../../state/selector';
import type { AnnotationAgentRenderPromptRequest } from '../../types/request';
import { buildAgentRenderPromptRequest } from '../../utils/buildAgentRenderPromptRequest';

// TODO: cleanup with cp_annotation_createproject_agent ff

const PREVIEW_DEBOUNCE_MS = 300;

interface DebouncedRequest {
  key: string;
  request: AnnotationAgentRenderPromptRequest | null;
}

interface UseAgentPromptPreviewResult {
  /** The last rendered prompt, kept while a newer one loads; null before the first response. */
  prompt: string | null;
  isError: boolean;
  /** A newer render is waiting for the debounce or in flight, so `prompt` may be stale. */
  isUpdating: boolean;
}

/**
 * Renders the prompt for the saved builder configuration. The request is keyed by its JSON
 * and sent 300 ms after the last change, so the latest configuration wins. Drafts in open
 * cards are never part of it: the preview updates when a card or section is saved.
 */
export const useAgentPromptPreview = (): UseAgentPromptPreviewResult => {
  const isAgentStepEnabled = useIsAgentStepEnabled();
  const conditionArea = useAppSelector(selectConditionArea);
  const addedConcepts = useAppSelector(selectAddedConcepts);
  const concepts = useAppSelector(selectAnnotationAgentConcepts);
  const sections = useAppSelector(selectAnnotationAgentSections);

  const request = useMemo(
    () => buildAgentRenderPromptRequest({ conditionArea, addedConcepts, concepts, sections }),
    [conditionArea, addedConcepts, concepts, sections]
  );
  const requestKey = useMemo(() => JSON.stringify(request), [request]);

  const [debounced, setDebounced] = useState<DebouncedRequest>({ key: requestKey, request });
  const isDebouncing = requestKey !== debounced.key;

  useEffect(() => {
    if (!isDebouncing) return undefined;
    const timer = setTimeout(() => setDebounced({ key: requestKey, request }), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isDebouncing, requestKey, request]);

  // With the flag off the step does not exist, so nothing is requested.
  const { data, isError, isFetching } = useRenderAnnotationAgentPromptQuery(
    isAgentStepEnabled && debounced.request ? debounced.request : skipToken
  );

  return {
    prompt: data?.data.prompt ?? null,
    isError,
    isUpdating: isFetching || isDebouncing,
  };
};
